import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { archiveTournament } from '@/api/TournamentManagement/tournamentApi';
import type { TournamentView } from '@/models/tournament';

interface DeleteTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament: TournamentView | null;
  onDeleted?: () => void;
}

const DeleteTournamentModal: React.FC<DeleteTournamentModalProps> = ({ isOpen, onClose, tournament, onDeleted }) => {
  const { t } = useTranslation('tournament');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);

  const statusKeys: Record<number, string> = {
    0: 'Upcoming',
    1: 'Active',
    2: 'Completed',
    3: 'Cancelled',
  };

  const handleDelete = async () => {
    if (!tournament) return;

    setIsDeleting(true);
    setApiError(null);

    try {
      const result = await archiveTournament(tournament.id);

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to delete tournament'));
        return;
      }

      setConfirmText('');
      onDeleted?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error deleting tournament:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isConfirmValid = confirmText.toLowerCase() === 'delete';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="delete-tournament-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="bg-red-50 border-b border-red-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-red-900 flex items-center">
              <Icon name="AlertTriangle" size={24} className="me-2 text-red-600" />
              {t('Delete Tournament')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-100 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-red-500/20"
              disabled={isDeleting}
              data-testid="delete-tournament-close-btn"
            >
              <Icon name="X" size={20} className="text-red-900" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* API Error */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={18} className="text-red-600 me-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{apiError}</p>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 me-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-900 mb-1">{t('Warning: This action cannot be undone')}</h3>
                <p className="text-sm text-red-700">
                  {t('Deleting this tournament will permanently remove all associated data, including matches, referee assignments, and historical records.')}
                </p>
              </div>
            </div>
          </div>

          {/* Tournament Details */}
          <div className="bg-gray-50 border border-border rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('Tournament to be deleted:')}</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('Name:')}</span>
                <span className="text-sm font-medium text-gray-900">{tournament?.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('ID:')}</span>
                <span className="text-sm font-medium text-gray-900">{tournament?.id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('Matches:')}</span>
                <span className="text-sm font-medium text-gray-900">{tournament?.matchCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('Status:')}</span>
                <span className="text-sm font-medium text-gray-900">
                  {tournament ? t(statusKeys[tournament.status] ?? 'Unknown') : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Type DELETE to confirm:')}
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-red-500/20 focus-visible:border-red-500 transition-colors"
              placeholder={t('Type DELETE')}
              disabled={isDeleting}
              data-testid="delete-tournament-confirm-input"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 rtl:space-x-reverse px-6 py-4 bg-gray-50 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
            data-testid="delete-tournament-cancel-btn"
          >
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleDelete}
            disabled={!isConfirmValid || isDeleting}
            loading={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="tournament-delete-btn"
          >
            {t('Delete Tournament')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteTournamentModal;
